import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";
import type { UserProfile } from "@/mocks/data";

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

const secciones = [
  { id: "s1", titulo: "TÍTULO I: Disposiciones Generales", articulos: 5, tipo: "seccion" },
  { id: "s2", titulo: "TÍTULO II: Deberes y Derechos", articulos: 8, tipo: "seccion" },
  { id: "s3", titulo: "TÍTULO III: Faltas y su Clasificación", articulos: 6, tipo: "seccion" },
  { id: "s4", titulo: "TÍTULO IV: Medidas Disciplinarias y Formativas", articulos: 7, tipo: "seccion" },
  { id: "s5", titulo: "TÍTULO V: Procedimientos y Debido Proceso", articulos: 5, tipo: "seccion" },
  { id: "s6", titulo: "Protocolo de Actuación frente a Violencia Escolar", articulos: 4, tipo: "protocolo" },
  { id: "s7", titulo: "Protocolo de Actuación frente a Acoso Escolar", articulos: 3, tipo: "protocolo" },
  { id: "s8", titulo: "Protocolo de Desregulación Emocional y Conductual", articulos: 3, tipo: "protocolo" },
  { id: "s9", titulo: "Mediación Escolar y Resolución Pacífica de Conflictos", articulos: 2, tipo: "protocolo" },
  { id: "s10", titulo: "Protocolo de Retención y Apoyo a la Trayectoria Educativa", articulos: 2, tipo: "protocolo" },
];

const faltas = [
  { tipo: "Faltas Leves", color: "accent", ejemplos: ["Llegadas atrasos reiterados", "No uso de uniforme", "Uso de celular en clases"] },
  { tipo: "Faltas Graves", color: "secondary", ejemplos: ["Agresiones verbales", "Destrucción de mobiliario", "Salir del establecimiento sin autorización"] },
  { tipo: "Faltas Muy Graves", color: "danger", ejemplos: ["Agresiones físicas", "Acoso escolar", "Porte de armas u objetos peligrosos"] },
];

export default function ReglamentoPage() {
  const navigate = useNavigate();
  const [user] = useState<UserProfile | null>(getStoredUser);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("indice");

  if (!user) return null;

  const filtered = secciones.filter((s) =>
    s.titulo.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Tabs */}
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
          {[
            { key: "indice", label: "Índice", icon: "ri-list-check" },
            { key: "articulos", label: "Artículos", icon: "ri-article-line" },
            { key: "protocolos", label: "Protocolos", icon: "ri-file-list-3-line" },
            { key: "faltas", label: "Faltas", icon: "ri-error-warning-line" },
            { key: "medidas", label: "Medidas", icon: "ri-scales-line" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-white text-primary-700 font-medium"
                  : "text-foreground-500 hover:text-foreground-700"
              }`}
            >
              <i className={`${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <Input
          placeholder="Buscar en el reglamento..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<i className="ri-search-line" />}
        />

        {/* Content based on active tab */}
        {activeTab === "indice" && (
          <div className="space-y-2">
            {filtered.map((seccion) => (
              <Card key={seccion.id} hover>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      seccion.tipo === "seccion" ? "bg-primary-100" : "bg-accent-100"
                    }`}>
                      <i className={`${
                        seccion.tipo === "seccion" ? "ri-book-2-line text-primary-600" : "ri-file-text-line text-accent-600"
                      } text-base`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{seccion.titulo}</p>
                      <p className="text-xs text-foreground-500">{seccion.articulos} artículos</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={() => navigate("/ia/consulta?q=" + encodeURIComponent(`¿Qué establece el reglamento interno en la sección: ${seccion.titulo}?`))}
                      variant="ghost" 
                      size="sm"
                      className="cursor-pointer"
                    >
                      <i className="ri-robot-line mr-1" /> Consultar IA
                    </Button>
                    <i className="ri-arrow-right-s-line text-foreground-400" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {activeTab === "faltas" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {faltas.map((falta) => {
              const colorMap: Record<string, { bg: string; text: string; border: string }> = {
                accent: { bg: "bg-accent-50", text: "text-accent-800", border: "border-accent-200" },
                secondary: { bg: "bg-secondary-50", text: "text-secondary-800", border: "border-secondary-200" },
                danger: { bg: "bg-red-50", text: "text-red-800", border: "border-red-200" },
              };
              const c = colorMap[falta.color];
              return (
                <Card key={falta.tipo} className={`${c.bg} border ${c.border}`}>
                  <h3 className={`text-sm font-semibold ${c.text} mb-3`}>{falta.tipo}</h3>
                  <ul className="space-y-1.5">
                    {falta.ejemplos.map((ej, i) => (
                      <li key={i} className="text-sm text-foreground-700 flex items-start gap-2">
                        <span className="text-foreground-400 mt-1.5">•</span>
                        {ej}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => navigate("/ia/consulta?q=" + encodeURIComponent(`¿Qué medidas formativas y disciplinarias se aplican para las ${falta.tipo}?`))}
                    variant="ghost" 
                    size="sm" 
                    className="mt-3 cursor-pointer"
                  >
                    <i className="ri-robot-line mr-1" /> Consultar sobre este tipo
                  </Button>
                </Card>
              );
            })}
          </div>
        )}

        {activeTab === "medidas" && (
          <div className="space-y-4">
            <Card>
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Medidas Formativas y Pedagógicas</h3>
              <p className="text-sm text-foreground-600 mb-3">
                Buscan la reflexión y el aprendizaje del estudiante, promoviendo cambios conductuales positivos.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {["Reflexión pedagógica guiada", "Trabajo comunitario interno", "Actividades de reparación", "Acompañamiento psicosocial", "Mediación escolar", "Plan de desarrollo personal"].map((m, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground-700 p-2 rounded-lg bg-accent-50">
                    <i className="ri-checkbox-circle-line text-accent-500" />
                    {m}
                  </div>
                ))}
              </div>
            </Card>
            <Card>
              <h3 className="text-sm font-semibold text-foreground-900 mb-3">Medidas Disciplinarias</h3>
              <p className="text-sm text-foreground-600 mb-3">
                Se aplican de forma gradual y proporcional a la falta cometida, siempre respetando el debido proceso.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {["Amonestación verbal", "Amonestación escrita", "Suspensión 1-3 días", "Suspensión 3-5 días", "Condicionalidad de matrícula", "No renovación de matrícula"].map((m, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground-700 p-2 rounded-lg bg-secondary-50">
                    <i className="ri-error-warning-line text-secondary-500" />
                    {m}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {activeTab === "protocolos" && (
          <div className="space-y-3">
            {secciones.filter((s) => s.tipo === "protocolo").map((p) => (
              <Card key={p.id} hover>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-text-line text-accent-600 text-base" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{p.titulo}</p>
                      <p className="text-xs text-foreground-500">{p.articulos} secciones</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={() => navigate("/ia/consulta?q=" + encodeURIComponent(`Explícame de forma sencilla y directa el: ${p.titulo}`))}
                      variant="ghost" 
                      size="sm"
                      className="cursor-pointer"
                    >
                      <i className="ri-lightbulb-line mr-1" /> Explicar simple
                    </Button>
                    <Button 
                      onClick={() => navigate("/ia/consulta?q=" + encodeURIComponent(`¿Cuáles son los pasos y plazos clave del ${p.titulo}?`))}
                      variant="ghost" 
                      size="sm"
                      className="cursor-pointer"
                    >
                      <i className="ri-robot-line mr-1" /> Consultar IA
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {activeTab === "articulos" && (
          <Card>
            <p className="text-sm text-foreground-500 text-center py-8">
              Selecciona una sección del índice para ver sus artículos, o usa el buscador para encontrar contenido específico.
            </p>
          </Card>
        )}
      </div>
    </>
  );
}